/* eslint-disable react/sort-prop-types */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isEqual, sortBy } from 'lodash';

const WithTranslations = (WrappedComponent) =>
  class WithTranslationsComponent extends Component {
    static manifest = Object.freeze({
      ...WrappedComponent.manifest,
      translationKeys: {
        type: 'okapi',
        path: 'translation-keys?limit=2147483647',
        records: 'translationKeys',
        accumulate: true,
        fetch: false,
        PUT: {
          path: 'translation-keys/%{translationKeyId}',
        },
      },
      languages: {
        type: 'okapi',
        path: 'languages?limit=1000',
        records: 'languages',
        accumulate: true,
        fetch: false,
        POST: {
          path: 'languages',
        },
        PUT: {
          path: 'languages/%{languageId}',
        },
      },
      languageId: '',
      translationKeyId: '',
    });

    static propTypes = {
      stripes: PropTypes.shape({
        connect: PropTypes.func.isRequired,
        okapi: PropTypes.object,
        locale: PropTypes.string,
      }).isRequired,
      resources: PropTypes.shape({
        translationKeys: PropTypes.shape({
          records: PropTypes.arrayOf(PropTypes.object),
        }),
        languageId: PropTypes.shape({
          replace: PropTypes.func.isRequired,
        }),
        translationKeyId: PropTypes.shape({
          replace: PropTypes.func.isRequired,
        }),
      }),
      mutator: PropTypes.shape({
        translationKeys: PropTypes.shape({
          GET: PropTypes.func.isRequired,
          reset: PropTypes.func.isRequired,
          PUT: PropTypes.func.isRequired,
        }),
        languages: PropTypes.shape({
          GET: PropTypes.func.isRequired,
          reset: PropTypes.func.isRequired,
          POST: PropTypes.func.isRequired,
          PUT: PropTypes.func.isRequired,
        }),
        languageId: PropTypes.shape({
          replace: PropTypes.func.isRequired,
        }),
        translationKeyId: PropTypes.shape({
          replace: PropTypes.func.isRequired,
        }),
      }),
    };

    state = {
      translationKeys: [],
      languages: [],
    };

    static getDerivedStateFromProps(nextProps, state) {
      const translationKeys = sortBy(
        (nextProps.resources.translationKeys || {}).records || [],
        ['localeValue'],
      );
      const languages = sortBy(
        (nextProps.resources.languages || {}).records || [],
        ['localeValue'],
      );
      const next = {};
      if (!isEqual(translationKeys, state.translationKeys)) {
        next.translationKeys = translationKeys;
      }
      if (!isEqual(languages, state.languages)) {
        next.languages = languages;
      }
      return Object.keys(next).length ? next : null;
    }

    componentDidMount() {
      this.props.mutator.translationKeys.GET();
      this.props.mutator.translationKeys.reset();
      this.props.mutator.languages.GET();
      this.props.mutator.languages.reset();
    }

    getLanguagesList = () => this.state.languages;

    /**
     * Returns { key: value } map for a given locale and category.
     * Consumed by LibTranslationsRoute / PermsTranslationsRoute.
     */
    getTranslations = (localeValue, categoryName) => {
      const result = {};
      this.state.translationKeys
        .filter(
          (tk) =>
            tk.localeValue === localeValue &&
            tk.categoryName === categoryName,
        )
        .forEach((tk) => {
          result[tk.translationKey] = tk.value || '';
        });
      return result;
    };

    /**
     * Returns the metadata of the first matching record (for ViewMetaData).
     */
    getTranslationsMetadata = (localeValue, categoryName) => {
      const tk = this.state.translationKeys.find(
        (t) =>
          t.localeValue === localeValue && t.categoryName === categoryName,
      );
      return tk ? tk.metadata || {} : {};
    };

    /**
     * Returns all translationKey records for a given category.
     */
    getCategoryLocales = (categoryName) => {
      return this.state.translationKeys.filter(
        (tk) => tk.categoryName === categoryName,
      );
    };

    /**
     * Returns distinct localeValues that have at least one record.
     */
    getSavedLocales = () => {
      return [
        ...new Set(
          this.state.translationKeys.map((tk) => tk.localeValue),
        ),
      ];
    };

    /**
     * Updates a single translation key via PUT /translation-keys/{id}.
     * kept for backward compat with PermsTranslationsRoute bulk update.
     */
    updateTranslations = (localeValue, categoryName, newTranslations) => {
      const { okapi } = this.props.stripes;
      const keys = Object.entries(newTranslations);
      const promises = keys.map(([translationKey, value]) => {
        const existing = this.state.translationKeys.find(
          (tk) =>
            tk.translationKey === translationKey &&
            tk.localeValue === localeValue,
        );
        if (existing) {
          return fetch(`${okapi.url}/translation-keys/${existing.id}`, {
            method: 'PUT',
            headers: {
              'X-Okapi-Tenant': okapi.tenant,
              'X-Okapi-Token': okapi.token,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ ...existing, value }),
          });
        }
        return fetch(`${okapi.url}/translation-keys`, {
          method: 'POST',
          headers: {
            'X-Okapi-Tenant': okapi.tenant,
            'X-Okapi-Token': okapi.token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            translationKey,
            categoryName,
            localeValue,
            value,
          }),
        });
      });
      Promise.all(promises).then(() => {
        this.props.mutator.translationKeys.reset();
        this.props.mutator.translationKeys.GET();
      });
    };

    updateLanguagesList = (newLangs) => {
      const languages = sortBy(
        (this.props.resources.languages || {}).records || [],
        ['localeValue'],
      );
      newLangs.forEach((newLang) => {
        const record = languages.find(
          (lang) => lang.localeValue === newLang.localeValue,
        );
        if (record) {
          this.props.mutator.languageId.replace(record.id);
          record.status = newLang.status;
          this.props.mutator.languages.PUT(record);
        } else {
          this.props.mutator.languages.POST({
            name: newLang.name,
            localeValue: newLang.localeValue,
            status: newLang.status,
          });
        }
      });
    };

    render() {
      return (
        <WrappedComponent
          getLanguagesList={this.getLanguagesList}
          getTranslations={this.getTranslations}
          getTranslationsMetadata={this.getTranslationsMetadata}
          getCategoryLocales={this.getCategoryLocales}
          getSavedLocales={this.getSavedLocales}
          updateTranslations={this.updateTranslations}
          updateLanguagesList={this.updateLanguagesList}
          {...this.props}
        />
      );
    }
  };

export default WithTranslations;
